import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import { toQueryString } from '@shared/api/query'
import {
  ConnectPayoutPageSchema,
  ConnectPayoutSchema,
  ConnectCompositionPageSchema,
} from './connect.contract'
import type { ConnectPayout } from './connect.contract'

export type ReceiptFilters = { currency?: 'BRL'; dateFrom?: string; dateTo?: string }
export type PayoutFilters = ReceiptFilters & { status?: ConnectPayout['status']; q?: string }
export const foregroundPolling = {
  refetchInterval: 15000,
  refetchIntervalInBackground: false,
} as const
export function connectCompositionQuery(id: string | null, enabled = true) {
  return infiniteQueryOptions({
    queryKey: ['wallet', 'connect-composition', id],
    queryFn: async ({ pageParam, signal }) =>
      ConnectCompositionPageSchema.parse(
        await apiFetch<unknown>(
          `/wallet/payouts/${encodeURIComponent(id!)}/composition${toQueryString({ cursor: pageParam ?? undefined, limit: 25 })}`,
          { signal },
        ),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => (page.hasMore ? page.nextCursor : undefined),
    enabled: id !== null && enabled,
    ...foregroundPolling,
  })
}
export function useConnectComposition(id: string | null, enabled = true) {
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  return useInfiniteQuery({
    ...connectCompositionQuery(id, enabled),
    ...(receiptsExperience ? { refetchInterval: false, retry: 1 } : {}),
  })
}
export function connectPayoutsQuery(filters: PayoutFilters = {}) {
  return infiniteQueryOptions({
    queryKey: ['wallet', 'connect-payouts', filters],
    queryFn: async ({ pageParam, signal }) =>
      ConnectPayoutPageSchema.parse(
        await apiFetch<unknown>(
          `/wallet/payouts${toQueryString({ ...filters, cursor: pageParam ?? undefined, limit: 25 })}`,
          { signal },
        ),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => (page.hasMore ? page.nextCursor : undefined),
    ...foregroundPolling,
  })
}
export function useConnectPayouts(filters: PayoutFilters = {}) {
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  return useInfiniteQuery({
    ...connectPayoutsQuery(filters),
    ...(receiptsExperience ? { refetchInterval: false, retry: 1 } : {}),
  })
}
export function useConnectPayout(id: string | null) {
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  return useQuery({
    queryKey: ['wallet', 'connect-payout', id],
    queryFn: async ({ signal }) =>
      ConnectPayoutSchema.parse(
        await apiFetch<unknown>(`/wallet/payouts/${encodeURIComponent(id!)}`, { signal }),
      ),
    enabled: id !== null,
    ...foregroundPolling,
    ...(receiptsExperience ? { refetchInterval: false, retry: 1 } : {}),
  })
}

export function useRefreshReceipts() {
  const client = useQueryClient()
  return () =>
    Promise.all(
      ['connect-payouts', 'connect-payout', 'connect-composition'].map((resource) =>
        client.invalidateQueries({ queryKey: ['wallet', resource] }),
      ),
    )
}
